/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import classNames from 'classnames';
import { CalculatedAttributeResult } from '../../calculatedAttributes/resolve';
import { useCalculatedAttributeResolver } from '../../calculatedAttributes/useCalculatedAttributeResolver';
import { useCtaResolver } from '../../components/ApplyCta';
import Button from '../../components/Button';
import { CmsImage } from '../../components/CmsImage';
import { MaterialIcon } from '../../components/MaterialIcon';
import { ScrollableWithArrows } from '../../components/ScrollableWithArrows';
import { BaseAttribute } from '../../types/attributes';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics/sendAnalytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatAttributeItemValue } from '../../utils/helpers/attrHelpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';
import { SimilarProductsProps } from './types';

const EMPTY_VALUE = '—';

const getAttributeValue = (
  attributes: BaseAttribute[] | undefined,
  attributeUid: string | undefined,
  calculated?: CalculatedAttributeResult,
): string => {
  // A calculated attribute has no catalogue value to find — the figure comes
  // from the engine, already formatted (CMS-1890).
  if (calculated) {
    return calculated.state === 'resolved'
      ? calculated.value.formatted
      : EMPTY_VALUE;
  }

  // Without a uid we can't match — bail out (otherwise find() would match the
  // first attribute whose uid is also undefined and return an unrelated value).
  if (!attributeUid) return EMPTY_VALUE;
  const attr = attributes?.find((a) => a?.name?.uid === attributeUid);
  // formatAttributeItemValue is typed string but returns `any` for some attr
  // types (JSON/RichText/…) — coerce before trim so it can't throw.
  const value = attr ? String(formatAttributeItemValue(attr) ?? '').trim() : '';
  return value || EMPTY_VALUE;
};

// CSS-grid layout (not a table): columns grow from min to max and a trailing
// 1fr track absorbs the leftover on wide screens, so columns cap at 244px with
// empty space on the right (Figma); narrower than the min sum → the row
// overflows (min-w-min) and the wrapper scrolls horizontally.
//   label:   minmax(120px, 250px)
//   product: minmax(112px, 244px)  (112px mobile min keeps "Apply Now" on one
//   line per designer; 244px desktop max)
const gridTemplateColumns = (productCount: number) => {
  // repeat(0, …) is invalid CSS — drop the product track group when empty.
  const productCols =
    productCount > 0 ? `repeat(${productCount}, minmax(112px,244px))` : '';
  return `minmax(120px,250px) ${productCols} 1fr`;
};

const ROW = 'grid w-full min-w-min items-stretch';

// Sticky first column so attribute labels stay visible while product columns
// scroll horizontally. The cell carries its own opaque background (matching the
// row) — the row background does not paint under a sticky cell that other cells
// scroll behind.
const LABEL_CELL =
  'sticky left-0 z-10 break-words px-spacing/padding/x-sm py-spacing/padding/y-sm text-left text-[15px] font-bold leading-[21px] text-text/primary';

export function SimilarProducts({
  module_title,
  cta_text_override,
  products,
  attributes,
  slug,
  pushDataLayer,
}: SimilarProductsProps) {
  const context = useCmsClientContext();
  const resolve = useCtaResolver();
  const resolveCalculated = useCalculatedAttributeResolver();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const isBlog = context.pageType === 'article';
  const title = module_title || staticText.default_title;
  const ctaText = cta_text_override || staticText.apply_now;
  const gridStyle = { gridTemplateColumns: gridTemplateColumns(products.length) };

  return (
    <section data-component="similar-products">
      <h2
        className={classNames(
          'mb-spacing/gap/y flex items-center gap-2.5 alpha-title-1',
          {
            'text-text/primary': isBlog,
            'text-text/accent py-spacing/padding/y-xs': !isBlog,
          },
        )}
      >
        {/* Standard layout shows an award_star icon before the title; blog does not. */}
        {!isBlog && (
          <MaterialIcon icon="award_star" px={30} className="shrink-0" />
        )}
        {title}
      </h2>

      <ScrollableWithArrows
        className={classNames(
          'overflow-x-auto rounded-radius/card bg-fill/card',
          // Standard: drop shadow (Figma). Blog: border (Figma blog variant).
          isBlog ? 'border border-borders/default' : 'shadow-md',
        )}
        recomputeKey={products.length}
        role="table"
        aria-label={title}
        prevLabel="Scroll to previous products"
        nextLabel="Scroll to next products"
      >
        {/* header row: product columns */}
        <div className={ROW} style={gridStyle} role="row">
          <div
            role="columnheader"
            className={classNames(LABEL_CELL, 'bg-fill/card')}
          />
          {products.map(({ product, cta_link_override }, productIndex) => (
            <div
              key={product.entryUID ?? productIndex}
              role="columnheader"
              data-product-id={product.uid ?? product.entryUID}
              className="flex flex-col items-center gap-2.5 px-spacing/padding/x-sm py-spacing/padding/y-sm"
            >
              <div className="flex flex-col items-center gap-1.5">
                {product.product_image && (
                  <CmsImage
                    src={product.product_image}
                    alt={product.product_name ?? product.title ?? ''}
                    width={64}
                    height={40}
                    sizes={{ _: 64 }}
                    className="h-10 w-16 rounded-[5px] object-contain"
                  />
                )}
                <div className="flex flex-col items-center gap-1.5 text-center">
                  <span className="text-sm font-bold leading-[18px] text-text/primary">
                    {product.provider?.provider_name}
                  </span>
                  <span className="text-sm leading-[18px] text-text/secondary">
                    {product.product_name ?? product.title}
                  </span>
                </div>
              </div>
              <Button
                as="a"
                styleType="cta"
                size="small"
                href={resolve({ product, ctaLinkOverride: cta_link_override })}
                target="_blank"
                rel="noreferrer noopener"
                onClick={() => {
                  if (!pushDataLayer) return;
                  sendProductAnalytics({
                    action: UserActionEnum.CLICK,
                    componentTitle: 'Similar Product/CTA_Apply Now',
                    componentType: ComponentTypeEnum.TABLE,
                    event: 'product_click_apply',
                    index: productIndex.toString(),
                    url: slug,
                    product,
                    pushDataLayer,
                  });
                }}
                // Figma: small-variant styling, regular height as a minimum
                // (button/size-regular = 44px) so a long custom label can wrap
                // to a 2nd line and grow instead of overflowing the column.
                className="mt-auto !h-auto !min-h-[--button-size-regular] w-fit whitespace-normal text-center"
              >
                {ctaText}
              </Button>
            </div>
          ))}
        </div>

        {/* attribute rows */}
        {attributes.map((row, index) => {
          const highlighted = index % 2 === 1;
          return (
            <div
              key={row.attribute.uid ?? index}
              role="row"
              className={classNames(ROW, {
                'bg-highlights/blue-light': highlighted,
              })}
              style={gridStyle}
            >
              <div
                role="rowheader"
                className={classNames(
                  LABEL_CELL,
                  highlighted ? 'bg-highlights/blue-light' : 'bg-fill/card',
                )}
              >
                {row.display_label_override || row.attribute.label}
              </div>
              {products.map(({ product }, productIndex) => (
                <div
                  key={product.entryUID ?? productIndex}
                  role="cell"
                  data-product-id={product.uid ?? product.entryUID}
                  className="break-words px-spacing/padding/x-xs py-spacing/padding/y-sm text-center text-sm leading-[18px] text-text/secondary"
                >
                  {getAttributeValue(
                    product.attributes,
                    row.attribute.uid,
                    resolveCalculated(row.attribute.name, product),
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </ScrollableWithArrows>
    </section>
  );
}
